import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../../assets';
import type { ModalDialogButtonSetting } from '../../common-interfaces';
import Dialog from '../../modal-dialog/dialog.component';

const userTimeZone = Intl.DateTimeFormat(navigator.language).resolvedOptions().timeZone;

export interface AboutInfoSchema {
  text: string;
}

export interface AboutInfoModalProps {
  version?: string;
  copyright?: string;
  currentSchema?: AboutInfoSchema;
  region?: string;
  cluster?: string;
  schemaTimeZone?: string;
  isAboutInfoOpen?: boolean;
  onClose?: () => void;
  onLogout?: () => void;
  showLogoutButton?: boolean;
  logoSource?: string;
  controllingSuperuser?: string;
  shouldCheckZIndex?: boolean;
  showUserTimeZone?: boolean;
  openSourceNotices?: string;
}

const AboutInfoModal = ({
  region = 'n/a',
  cluster = 'n/a',
  schemaTimeZone = 'n/a',
  copyright,
  currentSchema,
  isAboutInfoOpen,
  onClose,
  onLogout,
  showLogoutButton = true,
  logoSource,
  controllingSuperuser,
  version,
  showUserTimeZone = false,
  openSourceNotices,
}: AboutInfoModalProps) => {
  const { t } = useTranslation();

  const buttons = useMemo(() => {
    const buttonsArray = [
      {
        name: 'ok',
        text: t('ok', 'OK'),
        type: 'primary',
        closeModalOnClick: true,
      },
    ] as Array<ModalDialogButtonSetting>;

    if (showLogoutButton) {
      buttonsArray.unshift({
        name: 'logout',
        text: t('log-out', 'Log out'),
        type: 'text',
        closeModalOnClick: false,
        onClick: () => onLogout?.(),
      });
    }

    return buttonsArray;
  }, [showLogoutButton, onLogout, t]);

  return (
    <Dialog header={t('about', 'About')} visible={Boolean(isAboutInfoOpen)} onClose={onClose} buttons={buttons}>
      <div className="flex flex-col items-center gap-4 h-full px-6">
        <img src={logoSource || Logo.myoshLogoSquare} className="w-52" alt="Logo" />
        <div className="flex flex-col gap-2">
          <span>Version: {version}</span>
          <span>Current company: {currentSchema?.text || 'n/a'}</span>
          <span>Region: {region}</span>
          <span>Cluster: {cluster}</span>
          <span>Schema Time Zone: {schemaTimeZone}</span>
          {showUserTimeZone && <span>User Time Zone: {userTimeZone}</span>}
          {controllingSuperuser && <span>Controlling superuser: {controllingSuperuser}</span>}
          <span>{copyright || `copyright Netsight ${new Date().getFullYear()}`}</span>
          {openSourceNotices && <span className="mt-4 text-xs">{openSourceNotices}</span>}
        </div>
      </div>
    </Dialog>
  );
};

export default AboutInfoModal;
